import React, { useMemo } from 'react';
import { GroupListProps } from '@/types';
import { XMLGroupModel } from '@/models/XMLGroup';
import { GroupItem } from './GroupItem';
import { useAppContext } from '@/context/AppContext';

export const GroupList = React.memo<GroupListProps>(({ groupType, xmlDoc, vehicleData }) => {
  const { state } = useAppContext();
  
  const groups = useMemo(() => {
    if (!xmlDoc) return [];
    
    const groupElements = Array.from(xmlDoc.querySelectorAll(`${groupType} > Item`));
    return groupElements.map((element, index) => {
      const group = new XMLGroupModel(element, groupType);
      // Force refresh of models to ensure they're up to date
      group.refreshModels();
      return { group, index };
    });
  }, [xmlDoc, groupType]);

  // Filter groups based on search query
  const filteredGroups = useMemo(() => {
    const query = state.ui.searchQuery.toLowerCase().trim();
    
    if (!query) {
      return groups;
    }

    return groups.filter(({ group }) => {
      // Check if group name matches
      if (group.name.toLowerCase().includes(query)) {
        return true;
      }

      // Check if any model in the group matches
      return group.models.some(model => {
        const vehicle = vehicleData.get(model);
        const modelName = model.toLowerCase();
        const brandName = vehicle?.brand?.toLowerCase() || '';
        const vehicleName = vehicle?.name?.toLowerCase() || '';
        
        return modelName.includes(query) || 
               brandName.includes(query) || 
               vehicleName.includes(query);
      });
    });
  }, [groups, state.ui.searchQuery, vehicleData]);

  if (filteredGroups.length === 0) {
    if (state.ui.searchQuery) {
      return (
        <div className="group-list-empty">
          <p>No {groupType === 'vehGroups' ? 'vehicle' : 'pedestrian'} groups match your search.</p>
        </div>
      );
    }
    return (
      <div className="group-list-empty">
        <p>No {groupType === 'vehGroups' ? 'vehicle' : 'pedestrian'} groups found in the XML file.</p>
      </div>
    );
  }

  return (
    <div className="group-list">
      {filteredGroups.map(({ group, index }) => (
        <GroupItem
          key={`${groupType}-${index}-${group.name}`}
          group={group}
          vehicleData={vehicleData}
          groupType={groupType}
          groupIndex={index}
        />
      ))}
    </div>
  );
});

GroupList.displayName = 'GroupList';